import React from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Shield, Users, Award, Star, Mail, Phone, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const trustBadges = [
    { icon: Shield, text: "HIPAA Compliant", color: "text-green-500" },
    { icon: Award, text: "Medically Reviewed", color: "text-blue-500" },
    { icon: Users, text: "50,000+ Users", color: "text-purple-500" },
    { icon: Star, text: "4.9/5 Rating", color: "text-yellow-500" }
  ];

  const quickLinks = [
    { label: "How It Works", href: "#" },
    { label: "Features", href: "#" },
    { label: "Testimonials", href: "#" },
    { label: "Pricing", href: "#" },
    { label: "Blog", href: "#" },
    { label: "Help Center", href: "#" }
  ];

  const legalLinks = [
    { label: t('footer.privacy'), href: "#" },
    { label: t('footer.terms'), href: "#" },
    { label: "Cookie Policy", href: "#" },
    { label: "Data Protection", href: "#" }
  ];

  return (
    <footer className="bg-gradient-to-br from-purple-900 via-pink-800 to-indigo-900 text-white safe-area-bottom">
      <div className="mobile-container">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">THREEDOT</h3>
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                  ))}
                  <span className="text-sm text-purple-200 ml-1">4.9</span>
                </div>
              </div>
            </div>
            <p className="text-purple-200 leading-relaxed mb-6">
              Empowering women worldwide to understand, track, and celebrate their menstrual health journey with confidence and knowledge.
            </p>
            
            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-3">
              {trustBadges.map((badge, index) => (
                <motion.div
                  key={badge.text}
                  className="flex items-center space-x-2 p-2 bg-white/10 backdrop-blur-sm rounded-lg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ scale: 1.05 }}
                >
                  <badge.icon className={`w-4 h-4 ${badge.color}`} />
                  <span className="text-xs text-purple-100">{badge.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold mb-6 text-pink-200">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li
                  key={link.label}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.05 }}
                >
                  <a 
                    href={link.href} 
                    className="text-purple-200 hover:text-white transition-colors duration-300 text-sm hover:underline"
                  >
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Support & Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold mb-6 text-pink-200">Support & Legal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <motion.li
                  key={link.label}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.05 }}
                >
                  <a 
                    href={link.href} 
                    className="text-purple-200 hover:text-white transition-colors duration-300 text-sm hover:underline"
                  >
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>
            
            <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
              <h5 className="font-medium text-pink-200 mb-2">Medical Disclaimer</h5>
              <p className="text-xs text-purple-200 leading-relaxed">
                This app is for informational purposes only and should not replace professional medical advice. Always consult with healthcare providers for medical concerns.
              </p>
            </div>
          </motion.div>

          {/* Contact & Community */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-lg font-semibold mb-6 text-pink-200">Connect With Us</h4>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-purple-300" />
                <a href="mailto:support@threedot.app" className="text-purple-200 hover:text-white transition-colors text-sm">
                  support@threedot.app
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-purple-300" />
                <span className="text-purple-200 text-sm">24/7 Support Available</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-purple-300" />
                <span className="text-purple-200 text-sm">Global Community</span>
              </div>
            </div>

            {/* Community Stats */}
            <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <h5 className="font-medium text-pink-200 mb-3">Join Our Community</h5>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="text-lg font-bold text-white">50,000+</div>
                  <div className="text-xs text-purple-200">Active Users</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">120+</div>
                  <div className="text-xs text-purple-200">Countries</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">98%</div>
                  <div className="text-xs text-purple-200">Satisfaction</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">4.9★</div>
                  <div className="text-xs text-purple-200">App Rating</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="py-6 border-t border-white/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm text-purple-200">
              <div className="flex items-center space-x-2">
                <span>&copy; {currentYear} THREEDOT.</span>
                <span className="flex items-center">
                  {t('footer.made')} <Heart size={12} className="text-pink-400 fill-pink-400 mx-1" />
                  <span>for women, by Adshiya Maketing Labs
  </span>
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Language indicator */}
              <div className="flex items-center space-x-2 text-sm text-purple-200">
                <span>🌍</span>
                <span>Available in 3 languages</span>
              </div>
              
              {/* Social proof */}
              <div className="flex items-center space-x-2 text-sm text-purple-200">
                <div className="flex -space-x-1">
                  {['👩🏻‍💼', '👩🏽‍⚕️', '👩🏼‍🎓', '👩🏿‍💻'].map((avatar, i) => (
                    <div key={i} className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center border border-white/30">
                      <span className="text-xs">{avatar}</span>
                    </div>
                  ))}
                </div>
                <span>Trusted worldwide</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;